import { useEffect, useState } from "react";

/**
 * The engine references every code edition, state amendment, and design value;
 * the engineer decides which governs a given project. Each chip revolves through
 * the options it covers (codes by edition, states by adopted code, materials by family).
 */
// Intervals are staggered (and roughly co-prime) so the chips never flip in
// unison — each settles for ~5–9s, an unhurried cadence rather than a ticker.
const CHIPS: { label: string; values: string[]; interval: number }[] = [
  { label: "ASCE 7-", values: ["22", "16", "10"], interval: 5200 },
  { label: "NDS ", values: ["2024", "2018", "2015"], interval: 6100 },
  { label: "SDPWS ", values: ["2021", "2015"], interval: 6800 },
  { label: "AISC 360-", values: ["22", "16"], interval: 6400 },
  { label: "ACI 318-", values: ["19", "14"], interval: 7300 },
  { label: "TMS 402-", values: ["22", "16"], interval: 8200 },
  { label: "AISI S", values: ["240", "100"], interval: 8800 },
  { label: "IRC ", values: ["2024", "2021", "2018"], interval: 7000 },
  { label: "IBC ", values: ["2024", "2021", "2018"], interval: 7900 },
  // Revolving state-adopted codes (multi-state amendments).
  { label: "", values: ["CA · CBC", "FL · FBC", "OR · OSSC", "WA · IBC", "TX · IBC"], interval: 5600 },
  // Revolving material families — the engine sizes every member to its own code.
  { label: "", values: ["Sawn lumber", "Glulam", "Engineered wood", "Cold-formed steel", "Structural steel", "Concrete", "Masonry"], interval: 6700 },
];

function RevolvingChip({ label, values, interval }: { label: string; values: string[]; interval: number }) {
  const [i, setI] = useState(0);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (values.length < 2) return;
    let alive = true;
    const id = window.setInterval(() => {
      setFading(true);
      window.setTimeout(() => {
        if (!alive) return;
        setI((v) => (v + 1) % values.length);
        setFading(false);
      }, 260);
    }, interval);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [values.length, interval]);

  return (
    <span className="spec-chip">
      {label && <span className="spec-code-label">{label}</span>}
      <span className={`spec-code-ver${fading ? " is-fading" : ""}`}>{values[i]}</span>
    </span>
  );
}

export function SpecStrip() {
  return (
    <div className="landing-wrap">
      <div className="spec-strip">
        <span className="spec-validated">
          <span className="spec-validated-dot" aria-hidden="true" />
          Every calc traces to a published clause · validated by licensed PEs
        </span>
        <div className="spec-chips">
          {CHIPS.map((c) => (
            <RevolvingChip key={c.label + c.values[0]} label={c.label} values={c.values} interval={c.interval} />
          ))}
        </div>
        <span className="spec-note">
          References every code edition, state amendment, and design value; your engineer decides what governs.
        </span>
      </div>
    </div>
  );
}
